import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Paper } from '@material-ui/core';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';

import toast from '~/services/toast';
import api from '~/services/api';
import { formatCurrency, formatDate } from '~/utils/format';
import {
  API_BASE_URL,
  API_LOCAL_URL,
  API_NODE_URL,
  APPLICATION_MODE,
  APP_ERROR,
  APP_TERMINAIS,
  FILTRO_DATA,
  STATUS_MONITOR_PEDIDO,
  TIPO_DOCUMENTO,
  DOCUMENTO_SAIDA_REPLICA_SITUACAO
} from '~/constants';
import { DocumentoSaidaOnlineService } from '~/store/modules/documento-saida/documento-saida.service';
import { OrcamentoOnlineService } from '~/store/modules/orcamento/orcamento.service';
import { PedidoOnlineService } from '~/store/modules/pedido/pedido-service';
import { getApplicationIsOnline, isWeb } from '~/store/modules/app/app-selectors';
import { getFilterByData, getFilterNodeByData } from '~/store/modules/monitor/monitor-helper';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';
import * as monitorActions from '~/store/modules/monitor/monitor-actions';

import Searching from '~/components/Searching';
import ListEmpty from '~/components/ListEmpty';
import { calculaValorTotalItem } from '~/store/modules/pdv/pdv-helper';
import { selectorRotina } from '~/store/modules/auth/auth-selectors';
import { ordenarPorRelevancia, TIPO_PESQUISA } from '~/utils/weighted-search';

const useStyles = Mui.makeStyles({
  tableHead: {
    '& .MuiTableCell-root': {
      padding: '16px 8px',
    },
    '& .MuiTableCell-root:first-of-type': {
      paddingLeft: '16px',
    },
    '& .MuiTableCell-root:last-of-type': {
      paddingRight: '16px',
    },
  },
  TableBody: {
    '& .MuiTableCell-root': {
      padding: '2px 8px',
    },
    '& .MuiTableCell-root:first-of-type': {
      paddingLeft: '16px',
    },
    '& .MuiTableCell-root:last-of-type': {
      paddingRight: '16px',
    },
  },
});

const ModalImportarDocumento = ({ type, show, onClose, callback }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const location = useLocation();

  const rotinaF170DSA = useSelector(selectorRotina('F170DSA')) // F170DSA - Permite ao usuário copiar documento de saída já copiado.

  const { empresa, filiais } = useSelector(state => state.auth);
  const { terminal } = useSelector(state => state.app);
  const { selects } = useSelector(state => state.pdv);
  const isOnline = useSelector(getApplicationIsOnline());
  const [searchTerm, setSearchTerm] = useState('');

  const { loading, page, rowsPerPage, rowsPerPageOptions } = useSelector(state => state.monitor);

  const [selectedRows, setSelectedRows] = useState([]);
  const [filtroData, setFiltroData] = useState(FILTRO_DATA.HOJE);
  const [filtroFilial, setFiltroFilial] = useState(empresa.id);

  const filtroDataOptions = [
    {
      titulo: 'Hoje',
      filtro: FILTRO_DATA.HOJE
    },
    {
      titulo: 'Ontem',
      filtro: FILTRO_DATA.ONTEM
    },
    {
      titulo: 'Esta semana',
      filtro: FILTRO_DATA.ESTA_SEMANA
    },
    {
      titulo: 'Este mês',
      filtro: FILTRO_DATA.ESTE_MES
    },
    {
      titulo: 'Todos',
      filtro: FILTRO_DATA.TODOS
    }
  ];

  const filtroFiliaisOptions = filiais.map(filial => ({
    titulo: filial.fantasia,
    filtro: filial.id
  }));

  // const [terminais, setTerminais] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [totalDocs, setTotalDocs] = useState(null);
  const [ultimosDocumentos, setUltimosDocumentos] = useState([]);
  const [columns] = useState([
    { id: 'terminal', label: 'Terminal', width: 140 },
    { id: 'serieNumero', label: 'Série/Número', width: 120 },
    { id: 'dataEmissao', label: 'Emissão', width: 120 },
    { id: 'cliente', label: 'Cliente' },
    { id: 'operacaoFiscal', label: 'Operação fiscal' },
    { id: 'pagamento', label: 'Pagamento', width: 240 },
    { id: 'vendedor', label: 'Vendedor', width: 240 },
    { id: 'total', alignment: 'right', label: 'Total', width: 140 }
  ]);

  const selectedSomeRows = selectedRows.length > 0 && selectedRows.length < documentos.length;
  const selectedAllRows = selectedRows.length === documentos.length;

  const handleChangePage = (event, newPage) => {
    dispatch(monitorActions.setPage(newPage));
  };

  const handleChangeRowsPerPage = event => {
    dispatch(monitorActions.setRowsPerPage(parseInt(event.target.value, 10)));
    dispatch(monitorActions.setPage(0));
  };

  const getTitle = () => {
    const titles = {
      [TIPO_DOCUMENTO.ORCAMENTO]: 'Importação de orçamento',
      [TIPO_DOCUMENTO.PEDIDO]: 'Importação de pedido',
      [TIPO_DOCUMENTO.DOCUMENTOSAIDA]: 'Cópia de documento'
    };

    return titles[type];
  };

  const getTitleButtonSubmit = () => {
    const titles = {
      [TIPO_DOCUMENTO.ORCAMENTO]: 'Importar vários Orçamentos',
      [TIPO_DOCUMENTO.PEDIDO]: 'Importar vários Pedidos',
      [TIPO_DOCUMENTO.DOCUMENTOSAIDA]: 'Copiar vários documentos'
    };

    return titles[type];
  };

  const getSearchTitle = () => {
    const titles = {
      [TIPO_DOCUMENTO.ORCAMENTO]: 'Pesquisar orçamento...',
      [TIPO_DOCUMENTO.PEDIDO]: 'Pesquisar pedido...',
      [TIPO_DOCUMENTO.DOCUMENTOSAIDA]: 'Pesquisar documento...'
    };

    return titles[type];
  };

  const getSearchTooltip = () => {
    const titles = {
      [TIPO_DOCUMENTO.ORCAMENTO]: 'Buscar por nº do documento, cliente, vendedor ou valor...',
      [TIPO_DOCUMENTO.PEDIDO]: 'Buscar por nº do documento, cliente, vendedor, pedido de compra, contrato ou valor...',
      [TIPO_DOCUMENTO.DOCUMENTOSAIDA]: 'Buscar por nº do documento, cliente, vendedor, pedido de compra, contrato ou valor...'
    };

    return titles[type];
  };

  const loadDocumentos = async () => {
    dispatch(monitorActions.setLoading(true));
    try {
      let documentosOnline = [];
      // if (isOnline) {
      try {
        const pagination = {
          skip: page === 0 ? 0 : page * rowsPerPage,
          top: rowsPerPage
        };
        let filiaisIds = filiais?.map(x => x.id);
        if (filtroFilial !== null) {
          filiaisIds = [filtroFilial];
        }
        const filtros = [];
        if (filiais?.length > 0) {
          filtros.push({
            chave: 'intEmpresaId',
            valor: filiaisIds,
            operador: 'in'
          });
        }
        if (filtroData !== FILTRO_DATA.TODOS) {
          filtros.push(getFilterNodeByData(filtroData));
        }

        const service = new DocumentoSaidaOnlineService();
        const { data } = await service.getImportacao({
          offset: pagination.skip,
          limit: pagination.top,
          page,
          filtros
        });

        documentosOnline = [...data?.data?.map(x => ({ ...x, isOffline: false }))];
        if (data?.totalCount) {
          setTotalDocs(data.totalCount);
        }


          console.log({ documentosOnline });
        } catch (error) {
          console.error(error);
        }
      // }

      let documentosTerminais = [];

      setDocumentos([...documentosTerminais, ...documentosOnline]);
      setUltimosDocumentos(documentosOnline);
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      dispatch(monitorActions.setLoading(false));
    }
  };

  const loadPedidos = async () => {
    dispatch(monitorActions.setLoading(true));
    try {
      let pedidosOnline = [];
      // if (isOnline) {
      try {
        const pagination = {
          offset: page === 0 ? 0 : page * rowsPerPage,
          limit: rowsPerPage
        };
        const service = new PedidoOnlineService();
        const { data } = await service.getAll({
          status: STATUS_MONITOR_PEDIDO.ABERTOS,
          filtroData,
          ...pagination,
        });

        pedidosOnline = [...data?.pedidos?.map(x => ({ ...x, isOffline: false }))];
        setTotalDocs(data?.count ?? 0);

      } catch (error) {
        console.error(error);
      }
      // }

      let pedidosTerminais = [];
      if (!isWeb) {
        try {
          const terminais = JSON.parse(sessionStorage.getItem(APP_TERMINAIS) ?? '[]');
          if (terminais) {
            terminais.push({
              identificacaoTerminal: terminal,
              ipLocalTerminal: `${API_LOCAL_URL}`?.replace('http://', '')?.replace(':3335/api', ''),
              active: true
            });
          }

          for (const terminal of terminais) {
            try {
              await api.get(`http://${terminal.ipLocalTerminal}:3335/api`, {
                timeout: 100
              });
            } catch (error) {
              console.log(error?.message);
              if (error?.message === 'timeout of 100ms exceeded') {
                terminais[terminais.indexOf(terminal)].active = false;
              }
            }
          }

          const terminaisAtivos = terminais?.filter(x => x.active);

          const promises = terminaisAtivos?.map(x =>
            api.get(`http://${x.ipLocalTerminal}:3335/api/faturamento/pedido/monitor/abertos`)
          );
          const pedidos = (await Promise.race([...promises]))
            .map((item, index) => {
              return item?.value?.data?.pedidos
                ? item?.value?.data?.pedidos?.map(x => ({
                    ...x,
                    _terminal_importacao: terminaisAtivos[index],
                    isOffline: true
                  }))
                : [];
            })
            .flat();

          if (pedidos) {
            pedidosTerminais = [...pedidos];
          }
        } catch (error) {
          console.error(error);
        }
      }

      setDocumentos([...pedidosTerminais, ...pedidosOnline]);
      setUltimosDocumentos(pedidosOnline);
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      dispatch(monitorActions.setLoading(false));
    }
  };

  const loadOrcamentos = async () => {
    dispatch(monitorActions.setLoading(true));
    try {
      let orcamentosOnline = [];
      const pagination = {
        skip: page === 0 ? 0 : page * rowsPerPage,
        top: rowsPerPage
      };
      // if (isOnline) {
        try {
          const service = new OrcamentoOnlineService();
          const { data } = await service.getAll({
            ...pagination,
            filter: [...getFilterByData(filtroData), 'situacao eq 1']
          });

          if (data?.items?.length > 0) {
            orcamentosOnline = [...data?.items?.map(x => ({ ...x, isOffline: false }))];
          }
          if (data?.count) {
            setTotalDocs(data.count);
          }
        } catch (error) {
          console.error(error);
        }
      // }

      let orcamentosTerminais = [];
      if (!isWeb) {
        try {
          const terminais = JSON.parse(sessionStorage.getItem(APP_TERMINAIS) ?? '[]');
          if (terminais) {
            terminais.push({
              identificacaoTerminal: terminal,
              ipLocalTerminal: `${API_LOCAL_URL}`?.replace('http://', '')?.replace(':3335/api', ''),
              active: true
            });
          }

          for (const terminal of terminais) {
            try {
              await api.get(`http://${terminal.ipLocalTerminal}:3335/api`, {
                timeout: 100
              });
            } catch (error) {
              console.log(error?.message);
              if (error?.message === 'timeout of 100ms exceeded') {
                terminais[terminais.indexOf(terminal)].active = false;
              }
            }
          }
          const terminaisAtivos = terminais?.filter(x => x.active);
          const promises = terminaisAtivos?.map(
            async x =>
              await api.get(
                `http://${x.ipLocalTerminal}:3335/api/faturamento/orcamento/situacao/abertos`
              )
          );
          const _orcamentos = (await Promise.allSettled([...promises]))
            .map((item, index) => {
              console.log({ item });
              return item?.value?.data
                ? item?.value?.data?.map(x => ({
                    ...x,
                    _terminal_importacao: terminaisAtivos[index],
                    isOffline: true
                  }))
                : [];
            })
            .flat();

          orcamentosTerminais = [..._orcamentos];
        } catch (error) {
          console.error(error);
        }
      }

      setDocumentos([...orcamentosTerminais, ...orcamentosOnline]);
      setUltimosDocumentos([...orcamentosOnline]);
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      dispatch(monitorActions.setLoading(false));
    }
  };

  const loadData = () => {
    if (type === TIPO_DOCUMENTO.PEDIDO) {
      loadPedidos();
    } else if (type === TIPO_DOCUMENTO.ORCAMENTO) {
      loadOrcamentos();
    } else if (type === TIPO_DOCUMENTO.DOCUMENTOSAIDA) {
      loadDocumentos();
    }
  };

  const handleSelectDocumento = (_, id) => {
    setSelectedRows(prevSelected => {
      const _selectedRows = !prevSelected.includes(id)
        ? [...prevSelected, id]
        : [...prevSelected.filter(id => id !== id)];

      return _selectedRows;
    });
  };

  const handleSelectAllDocumentos = event => {
    setSelectedRows(event.target.checked ? documentos.map(item => item?.id ?? item?._id) : []);
  };

  const importarOrcamento = async documentos => {
    dispatch(
      pdvActions.importarOrcamento(documentos, location?.pathname, () => {
        if (onClose) onClose();
      })
    );
  };

  const importarPedido = async documentos => {
    let documentosSelecionados = [];
    const toastInstance = toast.loading('Carregando documento(s), aguarde ...');
    try {
      let documentosOnline = [];
      let documentosOffline = documentos?.filter(x => x?.isOffline);

      const documentosOnlineSelecionados = documentos?.filter(x => !x?.isOffline);
      if (isOnline) {
        documentosOnline =
          documentosOnlineSelecionados?.length > 0
            ? (
                await Promise.all([
                  ...documentosOnlineSelecionados?.map(x =>
                    api.get(`${API_BASE_URL}/v1/faturamento/pedido/${x.id}`)
                  )
                ])
              ).map(x => x.data)
            : [];
      }
      documentosSelecionados = [...documentosOffline, ...documentosOnline];
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      toastInstance.close();
    }

    if (documentosSelecionados?.length === 0) {
      return;
    }

    if (documentosSelecionados?.[0]?.documentoModelo === '65') {
      if (documentosSelecionados?.[0]?.intCliente) {
        const {
          enderecoLogradouro,
          enderecoLogradouroNumero,
          enderecoBairro,
          enderecoUf,
          enderecoIntCidadeId
        } = documentosSelecionados?.[0].intCliente;

        const {
          entregaLogradouro,
          entregaLogradouroNumero,
          entregaBairro,
          entregaUf,
          entregaIntCidadeId
        } = documentosSelecionados?.[0].intCliente;

        const condicaoQualquerCampoEnderecoCliente =
          enderecoLogradouro ||
          enderecoLogradouroNumero ||
          enderecoBairro ||
          enderecoUf ||
          enderecoIntCidadeId;
        const condicaoTodosCamposEnderecoCliente =
          enderecoLogradouro &&
          enderecoLogradouroNumero &&
          enderecoBairro &&
          enderecoUf &&
          enderecoIntCidadeId;
        const condicaoQualquerCampoEnderecoEntretega =
          entregaLogradouro ||
          entregaLogradouroNumero ||
          entregaBairro ||
          entregaUf ||
          entregaIntCidadeId;
        const condicaoTodosCamposEnderecoEntretega =
          entregaLogradouro &&
          entregaLogradouroNumero &&
          entregaBairro &&
          entregaUf &&
          entregaIntCidadeId;

        if (condicaoQualquerCampoEnderecoCliente && !condicaoTodosCamposEnderecoCliente) {
          toast.warning(
            'A NFC-e será gerada mas a informação de endereço do cliente não será adicionada pois os dados estão incompletos.'
          );
        }

        if (condicaoQualquerCampoEnderecoEntretega && !condicaoTodosCamposEnderecoEntretega) {
          toast.warning(
            'A NFC-e será gerada mas a informação de endereço do cliente não será adicionada pois os dados estão incompletos.'
          );
        }
      }

      const entregaUf =
        documentosSelecionados?.[0]?.entregaUf ?? documentosSelecionados?.[0]?.enderecoUf;
      if (entregaUf && empresa.uf !== entregaUf) {
        toast.error(
          'Não é permitido gerar <strong>NFC-e</strong> para destinátario de fora do estado.'
        );
        return;
      }

      if (
        documentosSelecionados?.[0]?.intCliente &&
        documentosSelecionados?.[0]?.intCliente?.tipoContribuinte === 1
      ) {
        toast.error(
          'O cliente do pedido selecionado é contribuinte de <strong>ICMS</strong>, portanto não é possível emitir <strong>NFC-e</strong>. É necessário emitir uma <strong>NF-e</strong>'
        );
        return;
      }
    }

    try {
      // if (isOnline) {
      //   const promisesBloqueios = documentosSelecionados.map(
      //     async documento =>
      //       await api.put(`${API_NODE_URL}/pdv/pedido/bloqueio/${documento?.id}/${terminal}`)
      //   );
      //   await Promise.all([...promisesBloqueios]);
      // }

      if (onClose) onClose();

      if (callback) {
        callback({
          tipoDocumento: TIPO_DOCUMENTO.DOCUMENTOSAIDA,
          venda: {
            // ...documentosSelecionados[0],
            // serie: null,
            // numero: null,
            id: null,
            _id: null,
            intCliente: documentosSelecionados?.[0]?.intCliente ?? null,
            intClienteId: documentosSelecionados?.[0]?.intCliente?.id ?? null,
            clienteNome: documentosSelecionados?.[0]?.clienteNome ?? null,
            clienteCpfCnpj: documentosSelecionados?.[0]?.clienteCpfCnpj ?? null,
            clienteTelefone: documentosSelecionados?.[0]?.clienteTelefone ?? null,
            clienteCelular: documentosSelecionados?.[0]?.clienteCelular ?? null,
            fatProfissional: documentosSelecionados?.[0]?.fatProfissional ?? null,
            fatProfissionalId: documentosSelecionados?.[0]?.fatProfissional?.id ?? null,
            entregaIntClienteEnderecoId:
              documentosSelecionados?.[0]?.entregaIntClienteEnderecoId ?? null,
            entregaCep: documentosSelecionados?.[0]?.entregaCep ?? null,
            entregaLogradouro: documentosSelecionados?.[0]?.entregaLogradouro ?? null,
            entregaLogradouroNumero: documentosSelecionados?.[0]?.entregaLogradouroNumero ?? null,
            entregaComplemento: documentosSelecionados?.[0]?.entregaComplemento ?? null,
            entregaBairro: documentosSelecionados?.[0]?.entregaBairro ?? null,
            entregaIntCidadeId: documentosSelecionados?.[0]?.entregaIntCidadeId ?? null,
            entregaIntCidade: documentosSelecionados?.[0]?.entregaIntCidade ?? null,
            entregaUf: documentosSelecionados?.[0]?.entregaUf ?? null,
            enderecoIntClienteEnderecoId:
              documentosSelecionados?.[0]?.enderecoIntClienteEnderecoId ?? null,
            enderecoCep: documentosSelecionados?.[0]?.enderecoCep ?? null,
            enderecoLogradouro: documentosSelecionados?.[0]?.enderecoLogradouro ?? null,
            enderecoLogradouroNumero: documentosSelecionados?.[0]?.enderecoLogradouroNumero ?? null,
            enderecoComplemento: documentosSelecionados?.[0]?.enderecoComplemento ?? null,
            enderecoBairro: documentosSelecionados?.[0]?.enderecoBairro ?? null,
            enderecoIntCidadeId: documentosSelecionados?.[0]?.enderecoIntCidadeId ?? null,
            enderecoIntCidade: documentosSelecionados?.[0]?.enderecoIntCidade ?? null,
            enderecoUf: documentosSelecionados?.[0]?.enderecoUf ?? null,
            fisOperacao: documentosSelecionados?.[0]?.fisOperacao ?? null,
            fisOperacaoId: documentosSelecionados?.[0]?.fisOperacao?.id ?? null,
            fatCondicaopagamento: documentosSelecionados?.[0]?.fatCondicaopagamento ?? null,
            fatCondicaopagamentoId: documentosSelecionados?.[0]?.fatCondicaopagamento?.id ?? null,
            fatMeiopagamento: documentosSelecionados?.[0]?.fatCondicaopagamento ?? null,
            fatCondicaopagamentoId: documentosSelecionados?.[0]?.fatCondicaopagamento?.id ?? null,
            fatVendedor: documentosSelecionados?.[0]?.fatVendedor ?? null,
            fatVendedorId: documentosSelecionados?.[0]?.fatVendedor?.id ?? null,
            informacaoComplementar: documentosSelecionados?.[0]?.informacaoComplementar ?? null,
            xPed: documentosSelecionados?.[0]?.xPed ?? null,
            xCont: documentosSelecionados?.[0]?.xCont ?? null,
            itens: [
              ...documentosSelecionados.map(documento => [
                ...documento?.itens
                  ?.filter(x => !x.itemCancelado)
                  ?.map(item => ({
                    ...item,
                    itemValorTotal: calculaValorTotalItem(item),
                    _terminal_importacao: documento?._terminal_importacao ?? null,
                    itemImportado: true
                  }))
              ])
            ]
              .flat()
              .map(item => ({
                ...item,
                id: null,
                fatPedidoItemId: item?.id ?? null,
                //offline
                _id: null,
                _parent: null,
                _id_orcamento_item: null,
                _id_pedido_item: item?._id ?? null
              })),
            parcelas:
              documentosSelecionados?.length === 1
                ? documentosSelecionados?.[0]?.parcelas?.map(parcela => ({
                    ...parcela,
                    id: null,
                    //offline
                    _id: null,
                    _parent: null
                  }))
                : [],
            importado: true
          }
        });
      }
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    }
  };

  const importarDocumento = async documentos => {
    let documentosSelecionados = [];
    const toastInstance = toast.loading('Carregando documento(s), aguarde ...');
    try {
      let documentosOnline = [];
      let documentosOffline = documentos?.filter(x => x?.isOffline);

      const documentosOnlineSelecionados = documentos?.filter(x => !x?.isOffline);
      if (isOnline) {
        documentosOnline =
          documentosOnlineSelecionados?.length > 0
            ? (
                await Promise.all([
                  ...documentosOnlineSelecionados?.map(x =>
                    api.get(
                      `${API_BASE_URL}/v1/faturamento/documentosaida/filiaiscompartilhadas/${x.id}`
                    )
                  )
                ])
              ).map(x => x.data)
            : [];
      }
      documentosSelecionados = [...documentosOffline, ...documentosOnline];
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      toastInstance.close();
    }

    if (documentosSelecionados?.length === 0) {
      return;
    }

    documentosSelecionados.forEach(doc => {
      doc.itens.forEach(item => {
        if (!item.saldoEstoque) {
          return (item.estoqueInsuficiente = true);
        }
        const estoqueDisponivel =
          item.saldoEstoque?.reduce((tot, item) => (tot += item.estoqueReal), 0) -
          item.saldoEstoque?.reduce((tot, item) => (tot += item.estoqueReservado), 0);
        if (estoqueDisponivel < item.itemQuantidade) {
          item.estoqueInsuficiente = true;
        }

        let itemAmbiente = item?.estProdutoAmbiente;
        const ambienteVazio = !itemAmbiente || Object.keys(itemAmbiente).length === 0;
        if (ambienteVazio && item?.estProdutoAmbienteId) {
          itemAmbiente = (selects?.ambientes ?? []).find(
            ambiente => ambiente.id === item.estProdutoAmbienteId
          );
        }
        item.estProdutoAmbiente = itemAmbiente ?? {id: null, descricao: 'NENHUM'};
        item.estProdutoAmbienteId = itemAmbiente?.id ?? null;

      });
    });

    const itens = [
      ...documentosSelecionados
        ?.map(documento =>
          documento?.itens ? documento?.itens?.filter(x => !x.itemCancelado) : []
        )
        .flat()
    ];
    const produtos = (
      await Promise.all(
        itens?.map(x => api.get(`${API_BASE_URL}/v1/estoque/produto/${x.produtoEstProdutoId}`))
      )
    )?.map(x => x.data);

    try {
      if (onClose) onClose();

      if (callback) {
        callback({
          tipoDocumento: TIPO_DOCUMENTO.DOCUMENTOSAIDA,
          venda: {
            id: null,
            _id: null,
            intCliente: documentosSelecionados?.[0]?.intCliente ?? null,
            intClienteId: documentosSelecionados?.[0]?.intCliente?.id ?? null,
            clienteNome: documentosSelecionados?.[0]?.clienteNome ?? null,
            clienteCpfCnpj: documentosSelecionados?.[0]?.clienteCpfCnpj ?? null,
            clienteTelefone: documentosSelecionados?.[0]?.clienteTelefone ?? null,
            clienteCelular: documentosSelecionados?.[0]?.clienteCelular ?? null,
            entregaIntClienteEnderecoId:
              documentosSelecionados?.[0]?.entregaIntClienteEnderecoId ?? null,
            entregaCep: documentosSelecionados?.[0]?.entregaCep ?? null,
            entregaLogradouro: documentosSelecionados?.[0]?.entregaLogradouro ?? null,
            entregaLogradouroNumero: documentosSelecionados?.[0]?.entregaLogradouroNumero ?? null,
            entregaComplemento: documentosSelecionados?.[0]?.entregaComplemento ?? null,
            entregaBairro: documentosSelecionados?.[0]?.entregaBairro ?? null,
            entregaIntCidadeId: documentosSelecionados?.[0]?.entregaIntCidadeId ?? null,
            entregaIntCidade: documentosSelecionados?.[0]?.entregaIntCidade ?? null,
            entregaUf: documentosSelecionados?.[0]?.entregaUf ?? null,
            enderecoIntClienteEnderecoId:
              documentosSelecionados?.[0]?.enderecoIntClienteEnderecoId ?? null,
            enderecoCep: documentosSelecionados?.[0]?.enderecoCep ?? null,
            enderecoLogradouro: documentosSelecionados?.[0]?.enderecoLogradouro ?? null,
            enderecoLogradouroNumero: documentosSelecionados?.[0]?.enderecoLogradouroNumero ?? null,
            enderecoComplemento: documentosSelecionados?.[0]?.enderecoComplemento ?? null,
            enderecoBairro: documentosSelecionados?.[0]?.enderecoBairro ?? null,
            enderecoIntCidadeId: documentosSelecionados?.[0]?.enderecoIntCidadeId ?? null,
            enderecoIntCidade: documentosSelecionados?.[0]?.enderecoIntCidade ?? null,
            enderecoUf: documentosSelecionados?.[0]?.enderecoUf ?? null,
            fisOperacao: documentosSelecionados?.[0]?.fisOperacao ?? null,
            fisOperacaoId: documentosSelecionados?.[0]?.fisOperacao?.id ?? null,
            fatCondicaopagamento: documentosSelecionados?.[0]?.fatCondicaopagamento ?? null,
            fatCondicaopagamentoId: documentosSelecionados?.[0]?.fatCondicaopagamento?.id ?? null,
            fatMeiopagamento: documentosSelecionados?.[0]?.fatCondicaopagamento ?? null,
            fatCondicaopagamentoId: documentosSelecionados?.[0]?.fatCondicaopagamento?.id ?? null,
            fatVendedor: documentosSelecionados?.[0]?.fatVendedor ?? null,
            fatVendedorId: documentosSelecionados?.[0]?.fatVendedor?.id ?? null,
            replicaFatDocumentosaidaId: documentosSelecionados?.[0]?.id ?? null,
            itens: itens.map((item, index) => ({
              ...item,
              id: null,
              omsModalidadeExpedicao: null,
              omsModalidadeExpedicaoId: null,
              index,
              fatPedidoItemId: null,
              fatOrcamentoItemId: null,
              produtoEstProduto: produtos?.find(x => x.id === item.produtoEstProdutoId),
              produtoEstArmazem: produtos?.find(x => x.id === item.produtoEstProdutoId)
                ?.estProdutoImposto[0]?.estoqueEstArmazem,
              produtoEstArmazemId: produtos?.find(x => x.id === item.produtoEstProdutoId)
                ?.estProdutoImposto[0]?.estoqueEstArmazemId,
              //offline
              _id: null,
              _parent: null,
              _id_orcamento_item: null,
              _id_pedido_item: null
            })),
            parcelas:
              documentosSelecionados?.length === 1
                ? documentosSelecionados?.[0]?.parcelas?.map(parcela => ({
                    ...parcela,
                    id: null,
                    _id: null,
                    _parent: null
                  }))
                : [],
            importado: true
          }
        });
      }
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    }
  };

  // Função auxiliar para buscar documentos online
  async function buscarDocumentosOnline(numeroDocumento) {
    if (!isOnline) return [];

    const endpointPedido = `${API_BASE_URL}/v1/faturamento/pedido/?$select=id&$filter=(numero eq ${numeroDocumento}) and (situacao eq 1)`;
    const endpointDocumentoSaida = `${API_NODE_URL}/faturamento/documento-saida/ids?$filter=${numeroDocumento}&data=${filtroData}&empresa=${filtroFilial}`;
    const endpointOrcamento = `${API_BASE_URL}/v1/faturamento/orcamento/?$select=id&$filter=(numero eq ${numeroDocumento})`;

    let url;
    if (type === TIPO_DOCUMENTO.PEDIDO) {
      url = endpointPedido;
    } else if (type === TIPO_DOCUMENTO.ORCAMENTO) {
      url = endpointOrcamento;
    } else {
      url = endpointDocumentoSaida;
    }

    try {
      const { data: { items } } = await api.get(url);
      if (items.length === 0) return [];

      const documentos = await Promise.all(items.map(({ id }) => buscarDocumentoPorId(id)));
      return documentos.map(x => ({ ...x, isOffline: false }));
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  // Função auxiliar para buscar documentos nos terminais
  async function buscarDocumentosTerminais(numeroDocumento) {
    const terminais = JSON.parse(sessionStorage.getItem(APP_TERMINAIS) ?? '[]');
    if (!terminais.length) return [];

    const promises = terminais.map(terminal =>
      buscarDocumentoTerminal(numeroDocumento, terminal)
    );

    const resultados = await Promise.allSettled(promises);
    return resultados.flatMap((resultado, index) =>
      resultado.status === 'fulfilled' ? adicionarInformacaoTerminal(resultado.value, terminais[index]) : []
    );
  }

  // Função auxiliar para buscar um documento por ID
  async function buscarDocumentoPorId(id) {
    const endpointPedido = `${API_BASE_URL}/v1/faturamento/pedido/${id}`;
    const endpointDocumentoSaida = `${API_NODE_URL}/faturamento/documento-saida/pdv/${id}/?empresa=${filtroFilial}`;
    const endpointOrcamento = `${API_BASE_URL}/v1/faturamento/orcamento/${id}`;

    let url;
    if (type === TIPO_DOCUMENTO.PEDIDO) {
      url = endpointPedido;
    } else if (type === TIPO_DOCUMENTO.ORCAMENTO) {
      url = endpointOrcamento;
    } else {
      url = endpointDocumentoSaida;
    }

    const { data } = await api.get(url);
    return data;
  }

  // Função auxiliar para buscar documento em um terminal específico
  async function buscarDocumentoTerminal(numeroDocumento, terminal) {
    const endpointPedido = `http://${terminal.ipLocalTerminal}:3335/api/faturamento/pedido/query/${numeroDocumento}`;
    const endpointOrcamento = `http://${terminal.ipLocalTerminal}:3335/api/faturamento/orcamento/query/${numeroDocumento}`;
    const url = type === TIPO_DOCUMENTO.PEDIDO ? endpointPedido : endpointOrcamento;

    try {
      const { data } = await api.get(url);
      return data.map(x => ({ ...x, isOffline: true, terminal: terminal.identificacaoTerminal }));
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  // Função auxiliar para adicionar informações do terminal
  function adicionarInformacaoTerminal(documentos, terminal) {
    return documentos.map(doc => ({
      ...doc,
      terminal: terminal.identificacaoTerminal,
      isOffline: true
    }));
  }

  // Função principal refatorada
  const handleSearch = async (goBackToFirstPage) => {
    try {
      dispatch(monitorActions.setLoading(true));

      let conditions = []
      let orderby = ''

      let tipoPesquisa = null
      if (/^\d+$/.test(searchTerm)) { // números
        tipoPesquisa = TIPO_PESQUISA.NUMEROS_INTEIROS
        switch (type) {
          case TIPO_DOCUMENTO.ORCAMENTO:
            orderby = `numero asc, valorTotal asc`
            conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
            conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
            break;
          case TIPO_DOCUMENTO.PEDIDO:
            orderby = `numero asc, xPed asc, xCont asc, valorTotal asc`
            conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
            conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
            conditions.push(`contains(xPed,'${searchTerm}')`)
            conditions.push(`contains(xCont,'${searchTerm}')`)
            break;
          case TIPO_DOCUMENTO.DOCUMENTOSAIDA:
            break;
        }
      }
      else if (/^(?=.*\d)(?=.*[.,])[\d.,]+$/.test(searchTerm)) { // numeros + ponto ou virgula
        tipoPesquisa = TIPO_PESQUISA.NUMEROS_FLUTUANTES
        switch (type) {
          case TIPO_DOCUMENTO.ORCAMENTO:
            orderby = `valorTotal asc`
            conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm.replace(/,/g, ".")}')`)
            break;
          case TIPO_DOCUMENTO.PEDIDO:
            orderby = `valorTotal asc`
            conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm.replace(/,/g, ".")}')`)
            break;
          case TIPO_DOCUMENTO.DOCUMENTOSAIDA:
            break;
        }
      }
      else if (/^[\p{L}\s]+$/u.test(searchTerm)) { // texto
        tipoPesquisa = TIPO_PESQUISA.TEXTO
        switch (type) {
          case TIPO_DOCUMENTO.ORCAMENTO:
            orderby = `clienteNome asc, fatVendedor/nome asc`
            conditions.push(`contains(clienteNome,'${searchTerm}')`)
            conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
            break;
          case TIPO_DOCUMENTO.PEDIDO:
            orderby = `clienteNome asc, fatVendedor/nome asc`
            conditions.push(`contains(clienteNome,'${searchTerm}')`)
            conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
            break;
          case TIPO_DOCUMENTO.DOCUMENTOSAIDA:
            break;
        }
      }
      else { // misto
        tipoPesquisa = TIPO_PESQUISA.MISTO
        switch (type) {
          case TIPO_DOCUMENTO.ORCAMENTO:
            orderby = `numero asc, clienteNome asc, fatVendedor/nome asc, valorTotal asc`
            conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
            conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
            conditions.push(`contains(clienteNome,'${searchTerm}')`)
            conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
            break;
          case TIPO_DOCUMENTO.PEDIDO:
            orderby = `numero asc, clienteNome asc, fatVendedor/nome asc, xPed asc, xCont asc`
            conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
            conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
            conditions.push(`contains(xPed,'${searchTerm}')`)
            conditions.push(`contains(xCont,'${searchTerm}')`)
            conditions.push(`contains(clienteNome,'${searchTerm}')`)
            conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
            break;
          case TIPO_DOCUMENTO.DOCUMENTOSAIDA:
            break;
        }
      }

      let filter = [conditions.length ? `(${conditions.join(' or ')})` : '']

      switch (type) {
        case TIPO_DOCUMENTO.ORCAMENTO:
          filter.push(...getFilterByData(filtroData, 'dataEmissao'))
          filter.push(`(situacao eq 1)`)
          break;
        case TIPO_DOCUMENTO.PEDIDO:
          filter.push(...getFilterByData(filtroData, 'dataEmissao'))
          filter.push(`(situacao eq 1)`)
          break;
        case TIPO_DOCUMENTO.DOCUMENTOSAIDA:
          break;
      }

      let newPage = goBackToFirstPage ? 0 : page

      let res
      switch (type) {
        case TIPO_DOCUMENTO.ORCAMENTO: {
          const service = new OrcamentoOnlineService();
          res = await service.getAll({
            skip: newPage === 0 ? 0 : newPage * rowsPerPage,
            top: rowsPerPage,
            filter: filter,
            orderby: orderby,
          });
          break;
        }
        case TIPO_DOCUMENTO.PEDIDO: {
          const service = new PedidoOnlineService();
          res = await service.getAllPedidos({
            skip: newPage === 0 ? 0 : newPage * rowsPerPage,
            top: rowsPerPage,
            filter: filter,
            orderby: orderby,
          });
          break;
        }
        case TIPO_DOCUMENTO.DOCUMENTOSAIDA: {
          const service = new DocumentoSaidaOnlineService();
          res = await service.getImportacaoSearch({
            offset: newPage === 0 ? 0 : newPage * rowsPerPage,
            limit: rowsPerPage,
            tempo: filtroData,
            filtroFilial: filtroFilial,
            pesquisaPor: searchTerm,
          });
          break;
        }
      }

      
      if (goBackToFirstPage) {
        dispatch(monitorActions.setPage(0));
      }
      
      setTotalDocs(res?.data?.count ?? 0);

      let items = ordenarPorRelevancia(res.data.items, searchTerm, type, tipoPesquisa)

      setDocumentos([...items]);

    } catch (err) {
      console.error(err);
      dispatch({ type: APP_ERROR, err });
    } finally {
      dispatch(monitorActions.setLoading(false));
    }
  };

  const handleSubmit = item => {
    if (type === TIPO_DOCUMENTO.ORCAMENTO) {
      importarOrcamento([item]);
    } else if (type === TIPO_DOCUMENTO.PEDIDO) {
      importarPedido([item]);
    } else if (type === TIPO_DOCUMENTO.DOCUMENTOSAIDA) {
      importarDocumento([item]);
    }
  };

  const handleSubmitAllDocumentos = () => {
    if (type === TIPO_DOCUMENTO.ORCAMENTO) {
      // importarOrcamento(selectedRows);
      importarOrcamento(documentos.filter(item => selectedRows.includes(item?._id ?? item?.id)));
    } else if (type === TIPO_DOCUMENTO.PEDIDO) {
      // importarPedido(selectedAllRows);
      importarPedido(documentos.filter(item => selectedRows.includes(item?._id ?? item?.id)));
    } else if (type === TIPO_DOCUMENTO.DOCUMENTOSAIDA) {
      importarDocumento(documentos.filter(item => selectedRows.includes(item?._id ?? item?.id)));
    }
  };

  useEffect(() => {
    if(searchTerm) {
      handleSearch(false)
    } 
    else {
      loadData();
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    // if (!terminais) {
    //   const _terminais = JSON.parse(sessionStorage.getItem(APP_TERMINAIS) ?? '[]');
    //   if (_terminais) {
    //     _terminais.push({
    //       identificacaoTerminal: terminal,
    //       ipLocalTerminal: `${API_LOCAL_URL}`?.replace('http://', '')?.replace(':3335/api', '')
    //     });
    //   }

    //   setTerminais(_terminais);
    // }
    if(searchTerm) {
      handleSearch(true)
    } 
    else {
      dispatch(monitorActions.setPage(0));
      loadData();
    }
  }, [filtroData, filtroFilial]);

  return (
    <Mui.Dialog open={show} onClose={onClose} PaperComponent={Paper} fullScreen>
      <Mui.DialogTitle>
        <Mui.AppBar color="default" position="fixed">
          <Mui.Toolbar>
            <Mui.IconButton color="inherit" onClick={onClose} aria-label="close" tabIndex={-1}>
              <MuiIcons.ArrowBack />
            </Mui.IconButton>
            <Mui.Box style={{ width: 320, paddingRight: 16 }}>
              <Mui.Typography variant="h5" style={{ textTransform: 'upperCase' }}>
                {getTitle()}
              </Mui.Typography>
            </Mui.Box>

            <Mui.Tooltip title={getSearchTooltip()}>
              <Mui.Box display="flex" width={'100%'}>
                <Mui.TextField
                  disabled={loading}
                  InputProps={{
                    style: {
                      height: 44
                    },
                    startAdornment: (
                      <Mui.InputAdornment position="start">
                        <Mui.SvgIcon fontSize="small" color="action">
                          <MuiIcons.Search />
                        </Mui.SvgIcon>
                      </Mui.InputAdornment>
                    )
                  }}
                  fullWidth
                  autoFocus
                  placeholder={getSearchTitle()}
                  variant="outlined"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                      if (searchTerm.trim() === '') {
                        setSearchTerm('')
                        dispatch(monitorActions.setPage(0));
                        loadData();
                      } else {
                        handleSearch(true);
                      }
                    }
                  }}
                />
              </Mui.Box>
            </Mui.Tooltip>

            {type === TIPO_DOCUMENTO.DOCUMENTOSAIDA && (
              <Mui.Select
                variant="outlined"
                style={{ width: 290, height: 44, marginLeft: 8 }}
                value={filtroFilial}
                onChange={e => setFiltroFilial(e.target.value)}
              >
                {filtroFiliaisOptions?.map((item, index) => (
                  <Mui.MenuItem key={index} value={item.filtro}>
                    {item.titulo}
                  </Mui.MenuItem>
                ))}
              </Mui.Select>
            )}

            <Mui.Select
              variant="outlined"
              style={{ width: 290, height: 44, marginLeft: 8 }}
              value={filtroData}
              onChange={e => setFiltroData(e.target.value)}
            >
              {filtroDataOptions?.map((item, index) => (
                <Mui.MenuItem key={index} value={item.filtro}>
                  {item.titulo}
                </Mui.MenuItem>
              ))}
            </Mui.Select>

            {/* {ultimosDocumentos?.length > 0 && (
              <Mui.Button
                variant="outlined"
                style={{ marginLeft: 8 }}
                onClick={() => {
                  setDocumentos([...ultimosDocumentos]);
                }}
              >
                <Mui.Badge color="error" badgeContent={ultimosDocumentos?.length} />
                <br />
                <Mui.Typography>Últimos documentos</Mui.Typography>
              </Mui.Button>
            )} */}
          </Mui.Toolbar>
        </Mui.AppBar>
      </Mui.DialogTitle>
      <Mui.DialogContent dividers>
        {(loading) ? (
          <>
            <Mui.Box style={{ marginTop: 64 }}>
              <Mui.LinearProgress variant="indeterminate" color="primary" />
            </Mui.Box>
            <Searching />
          </>
        ) : (
          <>
            {documentos?.length > 0 ? (
              <>
                <Mui.Table style={{ marginTop: 64 }}>
                  <Mui.TableHead className={classes.tableHead}>
                    {(type === TIPO_DOCUMENTO.ORCAMENTO || 
                      type === TIPO_DOCUMENTO.PEDIDO) && (
                        <Mui.TableCell align="center" style={{ width: 44 }}>
                          <Mui.Checkbox
                            checked={selectedAllRows}
                            color="primary"
                            indeterminate={selectedSomeRows}
                            onChange={handleSelectAllDocumentos}
                          />
                        </Mui.TableCell>
                      )
                    }
                    {columns.map((column, index) => (
                      <Mui.TableCell
                        key={`column-${index}`}
                        align={column?.alignment ?? 'left'}
                        style={{ width: column?.width ? `${column?.width}px` : 'auto' }}
                      >
                        {column?.label}
                      </Mui.TableCell>
                    ))}
                    <Mui.TableCell align="center" style={{ width: '160px' }}>
                      #
                    </Mui.TableCell>
                  </Mui.TableHead>
                  <Mui.TableBody className={classes.TableBody}>
                    {documentos?.map((item, index) => {
                      const isSelected = selectedRows.includes(item?._id ?? item?.id);

                      return (
                        <Mui.TableRow key={`documento-${index}`}>
                          {(type === TIPO_DOCUMENTO.ORCAMENTO || 
                            type === TIPO_DOCUMENTO.PEDIDO) && (
                              <Mui.TableCell align="center" style={{ width: 44 }}>
                                <Mui.Checkbox
                                  checked={isSelected}
                                  color="primary"
                                  value={isSelected}
                                  onChange={event => handleSelectDocumento(event, item?._id ?? item?.id)}
                                />
                              </Mui.TableCell>
                            )
                          }
                          <Mui.TableCell>{item?._terminal_importacao?.identificacaoTerminal ?? '-'}</Mui.TableCell>
                          <Mui.TableCell>
                            {item?.serie ?? item?.documentoSerie}/
                            {item?.numero ?? item?.documentoNumero}
                          </Mui.TableCell>
                          <Mui.TableCell>
                            <Mui.Typography variant="h6">
                              {item?.dataEmissao
                                ? formatDate(new Date(item?.dataEmissao))
                                : formatDate(new Date(item?.documentoDataEmissao))}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell>
                            <Mui.Typography variant="h6">
                              {item?.clienteNome ?? '-'}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell>
                            <Mui.Typography variant="h6">
                              {item?.fisOperacao?.descricao ?? '-'}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell>
                            <Mui.Typography variant="h6">
                              {item?.fatCondicaopagamento?.descricao}
                              {item?.fatCondicaopagamento?.descricao && <br />} 
                            </Mui.Typography>
                            <Mui.Typography variant="caption">
                              {(item?.parcelas?.length > 0) && (item?.parcelas?.map(x => x?.finMeiopagamento?.descricao)?.join(', '))}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell>
                            <Mui.Typography variant="h6">{item?.fatVendedor?.nome}</Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell align="right">
                            <Mui.Typography variant="h6">
                              {formatCurrency(item?.valorTotal)}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell>
                            <Mui.Tooltip title={(
                              rotinaF170DSA === false && item?.situacaoReplica === DOCUMENTO_SAIDA_REPLICA_SITUACAO.REPLICADO
                            ) ? <>
                              {rotinaF170DSA === false && item?.situacaoReplica === DOCUMENTO_SAIDA_REPLICA_SITUACAO.REPLICADO ?
                              'O documento já foi copiado!' : ''}
                            </> : ''}>
                              <Mui.Box>
                                <Mui.Button
                                  variant="contained"
                                  className="button-success"
                                  disabled={rotinaF170DSA === false && item?.situacaoReplica === DOCUMENTO_SAIDA_REPLICA_SITUACAO.REPLICADO}
                                  onClick={() => handleSubmit(item)}
                                >
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '2px'}}>
                                    {type === TIPO_DOCUMENTO.DOCUMENTOSAIDA ?
                                      <MuiIcons.FileCopy style={{position: 'relative', bottom: '2px'}}/> :
                                      <MuiIcons.ImportExport style={{position: 'relative', bottom: '2px'}}/>}
                                    <span style={{ verticalAlign: 'middle' }}>{type === TIPO_DOCUMENTO.DOCUMENTOSAIDA ? 'Copiar' : 'Importar'}</span>
                                  </span>
                                </Mui.Button>
                              </Mui.Box>
                            </Mui.Tooltip>
                          </Mui.TableCell>
                        </Mui.TableRow>
                      );
                    })}
                  </Mui.TableBody>
                </Mui.Table>
                <Mui.TablePagination
                  labelRowsPerPage={'Registros por página'}
                  rowsPerPageOptions={rowsPerPageOptions}
                  component="div"
                  count={totalDocs ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            ) : (
              <ListEmpty />
            )}
          </>
        )}
      </Mui.DialogContent>
      {(!loading) && (
        <Mui.DialogActions>
          <Mui.Box flex={1} display="flex" justifyContent="flex-end">
            <Mui.Box>
              {(type === TIPO_DOCUMENTO.ORCAMENTO ||
                type === TIPO_DOCUMENTO.PEDIDO) &&
                (selectedRows?.length > 0) && (
                  <Mui.Button variant="contained" className="button-success" onClick={() => handleSubmitAllDocumentos()}>
                    <MuiIcons.CheckCircle />
                    <Mui.Typography variant="inherit" component="span" style={{ marginLeft: 8 }}>
                      {getTitleButtonSubmit()}
                    </Mui.Typography>
                  </Mui.Button>
                )
              }
            </Mui.Box>
            <Mui.Button size="large" style={{ marginLeft: 8 }} onClick={onClose}>
              CANCELAR (Esc)
            </Mui.Button>
          </Mui.Box>
        </Mui.DialogActions>
      )}
    </Mui.Dialog>
  );
};

export default ModalImportarDocumento;
